import { defineMessage } from 'react-intl'

import Holidays from 'date-holidays'

import { Address } from '../../models/providers/Providers'
import {
  CONTACT_CARE_TEAM_REQUEST_TYPES,
  LYRIANS_SUPPORT_LINK,
  navigatorTicketTypes,
  PRODUCTION_DEFAULT_DIRECT_CARE_NAVIGATOR_BOOKING_LINK_BASE,
  STAGING_DEFAULT_DIRECT_CARE_ADVOCATE_BOOKING_LINK_BASE,
  STAGING_DEFAULT_DIRECT_CARE_NAVIGATOR_BOOKING_LINK_BASE,
} from '../common/constants/constants'
import { formatAddressForSubmission } from '../locationFinder/utils'

export const getCareNavigatorSchedulingLink = ({
  defaultDirectCareNavigatorBookingLink,
  directDedicatedCareNavigatorLink,
  launchDate,
  firstName,
  lastName,
  email,
  isZeroProvidersFlow = false,
  isAssertiveTriageFlow = false,
  salesforceId,
  isLyrian,
  isProd,
  isLyraForLyrians2026Enabled,
  isInternationalUser = false,
  lyraId,
  countryIsoCode,
  customerDisplayName,
}: {
  defaultDirectCareNavigatorBookingLink: string
  directDedicatedCareNavigatorLink: string
  launchDate: string
  firstName: string
  lastName?: string
  email: string
  isZeroProvidersFlow: boolean
  isAssertiveTriageFlow: boolean
  salesforceId: string
  isLyrian: boolean
  isProd: boolean
  isLyraForLyrians2026Enabled: boolean
  isInternationalUser?: boolean
  lyraId?: string
  countryIsoCode?: string
  customerDisplayName?: string
}): string => {
  if (isLyrian && !isLyraForLyrians2026Enabled) {
    return LYRIANS_SUPPORT_LINK
  }

  const launchYear = new Date(launchDate).getUTCFullYear()
  const useCareNavigatorLink = launchYear > 2022

  const environment = isProd ? 'production' : 'staging'
  const linkBases = {
    production: {
      navigator: PRODUCTION_DEFAULT_DIRECT_CARE_NAVIGATOR_BOOKING_LINK_BASE,
      advocate: defaultDirectCareNavigatorBookingLink,
    },
    staging: {
      navigator: STAGING_DEFAULT_DIRECT_CARE_NAVIGATOR_BOOKING_LINK_BASE,
      advocate: STAGING_DEFAULT_DIRECT_CARE_ADVOCATE_BOOKING_LINK_BASE,
    },
  }

  /**
   * In production: If the user has a dedicated care navigator schedule link, then
   * that is used. Otherwise, we have to use the default link for the
   * customer. The default link depends on the customer's launch date.
   * If the customer launched before 2023, we need to use the care advocate
   * link, which is set in the `defaultDirectCareNavigatorBookingLink` customer
   * property. If the customer launched on or after 1/1/2023, we need to use
   * the care navigator link, which is stored in a constant in shared-app-logic.
   *
   * On staging: If the user has a dedicated care navigator schedule link, then
   * that is used. For the default cases, can't use the same links that are used
   * in production so we have a test default care advocate link and a test default
   * care navigator link to use.
   */
  let link
  if (directDedicatedCareNavigatorLink) {
    link = directDedicatedCareNavigatorLink
  } else if (useCareNavigatorLink) {
    link = linkBases[environment].navigator
  } else {
    link = linkBases[environment].advocate
  }

  let params: Record<string, string> = {}

  if (isInternationalUser) {
    params = {
      company: customerDisplayName ?? '',
      firstName: firstName ?? '',
      lastName: lastName ?? '',
      lyraID: lyraId ?? '',
      countryISOCode: countryIsoCode ?? '',
      email,
    }
  } else if (salesforceId) {
    params = { sosfcontactid: salesforceId }
  } else {
    params = { name: firstName, email }
  }

  if (isZeroProvidersFlow) {
    params.param = 'zp'
  } else if (isAssertiveTriageFlow) {
    params.param = 'at'
  }

  const searchParams = new URLSearchParams(params)
  return `${link}?${searchParams.toString()}`
}

const formatNotes = ({
  typeOfRequest,
  contactCareNavigatorFormMessage,
  modalityPreference,
  selectedTreatmentOption,
  location,
  requestHeader,
}: {
  typeOfRequest: string
  contactCareNavigatorFormMessage: string
  modalityPreference: string
  selectedTreatmentOption: string | null
  location: string
  requestHeader?: string
}) => {
  switch (typeOfRequest) {
    case CONTACT_CARE_TEAM_REQUEST_TYPES.ASSERTIVE_TRIAGE_SCREENING:
      return `${typeOfRequest}, REQUEST: ${contactCareNavigatorFormMessage}`

    case CONTACT_CARE_TEAM_REQUEST_TYPES.AUTISM_SPECTRUM_DISORDER:
      return contactCareNavigatorFormMessage

    default: {
      const headerPrefix = requestHeader ? `${requestHeader}, ` : ''
      return `${headerPrefix}PREFERS_MODALITY[${modalityPreference}], COMING_FROM[${selectedTreatmentOption}], SEARCH_LOCATION[${location}] - ${contactCareNavigatorFormMessage}`
    }
  }
}

export const createCareNavigatorPayload = ({
  isLocationOnsite,
  locationAddress,
  formattedAddress,
  locationUserInput,
  contactCareNavigatorFormMessage,
  requestHeader,
  selectedTreatmentOption,
  modalityPreference,
  typeOfRequest,
}: {
  isLocationOnsite: boolean
  locationAddress?: Address
  formattedAddress: string
  locationUserInput: string
  contactCareNavigatorFormMessage: string
  requestHeader?: string
  selectedTreatmentOption: string | null
  modalityPreference: string
  typeOfRequest: string
}) => {
  const isSelectedLocationOnsite = isLocationOnsite
  const location = isSelectedLocationOnsite
    ? `ONSITE: ${locationAddress ? formatAddressForSubmission(locationAddress) : ''}`
    : formattedAddress || locationUserInput

  const notes = formatNotes({
    typeOfRequest,
    requestHeader,
    contactCareNavigatorFormMessage,
    modalityPreference,
    selectedTreatmentOption,
    location,
  })

  const data = {
    preferredAppointmentType: modalityPreference,
    notes,
    typeOfRequest: navigatorTicketTypes[typeOfRequest] ?? 'GET_OPTIONS',
  }
  return data
}

export const showDedicatedCareNavigatorSection = ({
  headShotURL,
  isDedicatedCareNavigatorEnabled,
  memberBio,
}: {
  headShotURL: string
  isDedicatedCareNavigatorEnabled: boolean
  memberBio: string
}): boolean => {
  return isDedicatedCareNavigatorEnabled && Boolean(memberBio) && Boolean(headShotURL)
}

// List of Lyra-sponsored holidays.
// Names directly taken from `Holidays.getHolidays` from the `date-holidays` library.
const lyraObservedHolidays = [
  "New Year's Day",
  'Martin Luther King Jr. Day',
  "Presidents' Day",
  'Memorial Day',
  'Juneteenth',
  'Independence Day',
  'Labor Day',
  'Thanksgiving Day',
  'Day after Thanksgiving Day',
  'Christmas Eve',
  'Christmas Day',
  "New Year's Eve",
]

export const isDayUSLyraObservedHoliday = ({ day }: { day: Date }) => {
  // Initialize holidays in US + CA time.
  // `date-holidays` properly processes `day` as the correct time zone, i.e. a Date of
  // 12/26 7:00 AM in GMT will be considered Christmas Day in PT (12/25 11:00 PM in GMT-8)
  const holidays = new Holidays('US', 'CA')
  return lyraObservedHolidays.includes(holidays.isHoliday(day)?.[0]?.name)
}

export const checkBusinessHours = () => {
  const businessHoursTimeZone = 'America/Los_Angeles'
  const businessHours = [
    { day: 'Monday', from: '06:00', to: '18:00' },
    { day: 'Tuesday', from: '06:00', to: '18:00' },
    { day: 'Wednesday', from: '06:00', to: '18:00' },
    { day: 'Thursday', from: '06:00', to: '18:00' },
    { day: 'Friday', from: '06:00', to: '18:00' },
  ]

  const now = new Date()
  const currentDayInWeek = now.toLocaleString('en-US', {
    timeZone: businessHoursTimeZone,
    weekday: 'long',
  })
  const currentTime = new Intl.DateTimeFormat([], {
    timeZone: businessHoursTimeZone,
    hour12: false,
    hour: '2-digit',
    minute: '2-digit',
  }).format(now)

  const workingTimes = businessHours.filter((times) => times.day === currentDayInWeek)

  const isWithinBusinessHours = workingTimes.some((time) => {
    if (currentTime >= time.from && currentTime <= time.to) return true
    return false
  })
  const isLyraObservedHoliday = isDayUSLyraObservedHoliday({ day: now })

  return isWithinBusinessHours && !isLyraObservedHoliday
}

export const getCareNavigatorLabel = ({
  isNeurodiversityFlowAndASDCoordinationEnabled,
}: {
  isNeurodiversityFlowAndASDCoordinationEnabled: boolean
}) => {
  if (isNeurodiversityFlowAndASDCoordinationEnabled) {
    return defineMessage({
      defaultMessage: 'neurodiversity support team',
      description:
        'Care team who specializes in neurodiversity and helps users navigate their search for mental health care',
    })
  } else {
    return defineMessage({
      defaultMessage: 'Care Navigator',
      description: 'Care team member who helps users navigate their search for mental health care',
    })
  }
}

export const getCareNavigatorLabelPlural = ({
  isNeurodiversityFlowAndASDCoordinationEnabled,
}: {
  isNeurodiversityFlowAndASDCoordinationEnabled: boolean
}) => {
  if (isNeurodiversityFlowAndASDCoordinationEnabled) {
    return defineMessage({
      defaultMessage: 'neurodiversity support team',
      description:
        'Care team who specializes in neurodiversity and helps users navigate their search for mental health care',
    })
  } else {
    return defineMessage({
      defaultMessage: 'Care Navigators',
      description: 'Plural label for Care Navigator in body text',
    })
  }
}

export const getCareNavigatorLabelPluralWithMember = ({
  isNeurodiversityFlowAndASDCoordinationEnabled,
}: {
  isNeurodiversityFlowAndASDCoordinationEnabled: boolean
}) => {
  if (isNeurodiversityFlowAndASDCoordinationEnabled) {
    return defineMessage({
      defaultMessage: 'neurodiversity support team members',
      description: 'Plural label for neurodiversity support team members',
    })
  } else {
    return defineMessage({
      defaultMessage: 'Care Navigators',
      description: 'Plural label for Care Navigator in body text',
    })
  }
}

export const getCareNavigatorLabelSingularWithMember = ({
  isNeurodiversityFlowAndASDCoordinationEnabled,
}: {
  isNeurodiversityFlowAndASDCoordinationEnabled: boolean
}) => {
  if (isNeurodiversityFlowAndASDCoordinationEnabled) {
    return defineMessage({
      defaultMessage: 'neurodiversity support team member',
      description: 'Singular label for neurodiversity support team member',
    })
  } else {
    return defineMessage({
      defaultMessage: 'Care Navigator',
      description: 'Singular label for Care Navigator',
    })
  }
}

export const getCareNavigatorLabelWithArticle = ({
  isNeurodiversityFlowAndASDCoordinationEnabled,
}: {
  isNeurodiversityFlowAndASDCoordinationEnabled: boolean
}) => {
  if (isNeurodiversityFlowAndASDCoordinationEnabled) {
    return defineMessage({
      defaultMessage: 'our neurodiversity support team',
      description:
        'Care team who specializes in neurodiversity and helps users navigate their search for mental health care - with possessive article',
    })
  } else {
    return defineMessage({
      defaultMessage: 'a Care Navigator',
      description:
        'Care team member who helps users navigate their search for mental health care - with indefinite article',
    })
  }
}
